import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type PointerEvent } from "react";
import { Button, Checkbox, Label, Spinner, TextInput } from "flowbite-react";
import ReactSelect from "react-select";
import { toast } from "react-toastify";
import CommonApi from "../../Api/CommonApi";
import EventApi, { ContactSessionBooth } from "../../Api/EventApi";
import MemberApi from "../../Api/MemberApi";
import { parseBoothRegion, serializeBoothRegion } from "../../Models/BoothRegion";
import { solidMenuReactSelectProps } from "../../Utils/reactSelectStyles";

type MemberOption = { value: number; label: string };

export type ContactSessionEditorHandle = {
  saveDraft: (eventId: number) => Promise<void>;
};

function readError(err: unknown, fallback: string) {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data && typeof data === "object" && "message" in data) {
    const message = (data as { message?: string }).message;
    if (message) return message;
  }
  if (data && typeof data === "object" && "detail" in data) {
    const detail = (data as { detail?: string }).detail;
    if (detail) return detail;
  }
  if (data && typeof data === "object" && "title" in data) {
    const title = (data as { title?: string }).title;
    if (title && title !== "Bad Request") return title;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

const ContactSessionEditor = forwardRef<
  ContactSessionEditorHandle,
  { eventId: number; ensureEventId: () => Promise<number> }
>(function ContactSessionEditor({ eventId, ensureEventId }, ref) {
  const [memberOptions, setMemberOptions] = useState<MemberOption[]>([]);
  const [floorplanUrl, setFloorplanUrl] = useState<string>("");
  const [booths, setBooths] = useState<ContactSessionBooth[]>([]);
  const [busy, setBusy] = useState(false);
  const [readingMap, setReadingMap] = useState(false);
  const [rowQuery, setRowQuery] = useState("");
  const [unmatchedOnly, setUnmatchedOnly] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [drawing, setDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null);
  const [drawCurrent, setDrawCurrent] = useState<{ x: number; y: number } | null>(null);
  const reading = useRef(false);
  const mapRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    saveDraft: async (id: number) => {
      if (!floorplanUrl && booths.length === 0) return;
      try {
        await EventApi.saveContactSession(id, { floorplanUrl, booths });
      } catch (err) {
        throw new Error(readError(err, "Could not save the contact session map."));
      }
    },
  }), [floorplanUrl, booths]);

  useEffect(() => {
    MemberApi.getMemberOptions()
      .then((res) => {
        const options = (res.data ?? [])
          .filter((member) => member.id && member.name)
          .map((member) => ({ value: member.id, label: member.name }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setMemberOptions(options);
      })
      .catch((err) => {
        console.error(err);
        setMemberOptions([]);
        toast.error("Could not load members for the dropdown.");
      });
  }, []);

  const optionById = useMemo(() => {
    const map = new Map<number, MemberOption>();
    memberOptions.forEach((option) => map.set(option.value, option));
    return map;
  }, [memberOptions]);

  useEffect(() => {
    if (!eventId || reading.current) return;
    EventApi.getContactSessionMap(eventId)
      .then((res) => {
        setFloorplanUrl(res.data.floorplanUrl ?? "");
        setBooths(res.data.booths ?? []);
      })
      .catch(() => {
        setFloorplanUrl("");
        setBooths([]);
      });
  }, [eventId]);

  const uploadAndRead = async (file: File) => {
    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    reading.current = true;
    setBusy(true);
    setReadingMap(true);
    try {
      const id = await ensureEventId();
      if (isPdf) {
        const fd = new FormData();
        fd.append("file", file);
        const parsed = await EventApi.parseContactSessionFile(id, fd);
        setFloorplanUrl(parsed.data.floorplanUrl ?? "");
        setBooths(parsed.data.booths ?? []);
        toast.success(`Read ${parsed.data.booths?.length ?? 0} companies from the PDF. Review them, then save the map.`);
        return;
      }
      const fd = new FormData();
      fd.append("file", file);
      const uploadRes = await CommonApi.addImageFile(fd);
      const url = (uploadRes.data as { url?: string })?.url ?? "";
      if (!url) {
        toast.error("Upload did not return an image URL.");
        return;
      }
      const parsed = await EventApi.parseContactSession(id, url);
      setFloorplanUrl(parsed.data.floorplanUrl ?? url);
      setBooths(parsed.data.booths ?? []);
      toast.success(`Read ${parsed.data.booths?.length ?? 0} companies. Review them, then save the map.`);
    } catch (err) {
      console.error(err);
      if (!(err instanceof Error && err.name === "ValidationError")) {
        toast.error(readError(err, "Could not read the floorplan."));
      }
    } finally {
      reading.current = false;
      setBusy(false);
      setReadingMap(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const id = eventId || (await ensureEventId());
      await EventApi.saveContactSession(id, { floorplanUrl, booths });
      toast.success("Contact session map saved.");
    } catch (err) {
      console.error(err);
      toast.error(readError(err, "Could not save the map."));
    } finally {
      setBusy(false);
    }
  };

  const updateBooth = (index: number, patch: Partial<ContactSessionBooth>) => {
    setBooths((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addCompany = () => {
    const index = booths.length;
    setBooths((rows) => [
      ...rows,
      { label: "", displayName: "", memberId: null, regionJson: null },
    ]);
    setSelectedIndex(index);
    setUnmatchedOnly(false);
    setRowQuery("");
  };

  const getPercentPoint = useCallback((clientX: number, clientY: number) => {
    const el = mapRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    return {
      x: ((clientX - rect.left) / rect.width) * 100,
      y: ((clientY - rect.top) / rect.height) * 100,
    };
  }, []);

  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (selectedIndex == null) return;
    const pt = getPercentPoint(e.clientX, e.clientY);
    if (!pt) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrawing(true);
    setDrawStart(pt);
    setDrawCurrent(pt);
  };

  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!drawing) return;
    const pt = getPercentPoint(e.clientX, e.clientY);
    if (pt) setDrawCurrent(pt);
  };

  const handlePointerUp = (e: PointerEvent<HTMLDivElement>) => {
    if (!drawing || drawStart == null || drawCurrent == null || selectedIndex == null) {
      setDrawing(false);
      setDrawStart(null);
      setDrawCurrent(null);
      return;
    }
    const left = Math.min(drawStart.x, drawCurrent.x);
    const top = Math.min(drawStart.y, drawCurrent.y);
    const width = Math.abs(drawCurrent.x - drawStart.x);
    const height = Math.abs(drawCurrent.y - drawStart.y);
    if (width > 0.5 && height > 0.5) {
      updateBooth(selectedIndex, {
        regionJson: serializeBoothRegion({
          shape: "rect",
          coords: [
            Math.round(left * 100) / 100,
            Math.round(top * 100) / 100,
            Math.round(width * 100) / 100,
            Math.round(height * 100) / 100,
          ],
        }),
      });
    }
    setDrawing(false);
    setDrawStart(null);
    setDrawCurrent(null);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const previewRect =
    drawing && drawStart && drawCurrent
      ? {
          left: Math.min(drawStart.x, drawCurrent.x),
          top: Math.min(drawStart.y, drawCurrent.y),
          width: Math.abs(drawCurrent.x - drawStart.x),
          height: Math.abs(drawCurrent.y - drawStart.y),
        }
      : null;

  const visibleBooths = booths
    .map((booth, index) => ({ booth, index }))
    .filter(({ booth }) => {
      if (unmatchedOnly && booth.memberId) return false;
      const q = rowQuery.trim().toLowerCase();
      if (!q) return true;
      const memberName = booth.memberId ? optionById.get(booth.memberId)?.label ?? "" : "";
      return (
        (booth.label ?? "").toLowerCase().includes(q) ||
        (booth.displayName ?? "").toLowerCase().includes(q) ||
        memberName.toLowerCase().includes(q)
      );
    });
  const matchedCount = booths.filter((booth) => booth.memberId).length;

  return (
    <div className="border rounded p-3 space-y-3 bg-gray-50">
      <div className="font-semibold">Contact session map</div>
      <p className="text-sm text-gray-600">
        Upload the PDF so each box sits on the company name. A PNG or JPEG still works, but those boxes are only a rough guess. Pick a member or type the name to show, then click Save.
      </p>
      <input
        type="file"
        accept=".pdf,application/pdf,image/png,image/jpeg,image/webp"
        disabled={busy}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) uploadAndRead(file);
          e.target.value = "";
        }}
      />
      {readingMap && (
        <div className="flex items-center gap-3 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
          <Spinner size="md" />
          <span>Reading the floorplan and placing each company.</span>
        </div>
      )}
      {floorplanUrl && (
        <div>
          <p className="text-xs text-gray-600 mb-2">
            {selectedIndex == null
              ? "Click a company in the list, then drag on the map to redraw its box. Use Add company for one the reader missed."
              : `Drag on the map to place the box for ${booths[selectedIndex]?.label || "the new company"}. Then click Save.`}
          </p>
          <div
            ref={mapRef}
            className="relative inline-block max-w-full select-none touch-none border rounded bg-white"
            style={{ cursor: selectedIndex != null ? "crosshair" : "default" }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          >
            <img
              src={floorplanUrl}
              alt="Contact session floorplan"
              className="block max-w-full h-auto pointer-events-none"
              draggable={false}
            />
            {booths.map((booth, index) => {
              const region = parseBoothRegion(booth.regionJson ?? undefined);
              if (!region) return null;
              const [left, top, width, height] = region.coords;
              const isSelected = selectedIndex === index;
              return (
                <div
                  key={`${booth.label}-${index}`}
                  className={`absolute border-2 box-border pointer-events-none ${
                    isSelected ? "border-blue-700 bg-blue-500/30" : "border-green-700 bg-green-500/20"
                  }`}
                  style={{ left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%` }}
                  title={booth.displayName || booth.label}
                />
              );
            })}
            {previewRect && (
              <div
                className="absolute border-2 border-dashed border-blue-600 bg-blue-400/20 pointer-events-none"
                style={{
                  left: `${previewRect.left}%`,
                  top: `${previewRect.top}%`,
                  width: `${previewRect.width}%`,
                  height: `${previewRect.height}%`,
                }}
              />
            )}
          </div>
        </div>
      )}
      {floorplanUrl && (
        <div className="space-y-2">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-gray-700">
              {matchedCount} of {booths.length} matched
              {memberOptions.length === 0 ? ". Members are still loading." : "."}
            </p>
            <Button type="button" color="light" size="xs" onClick={addCompany}>
              Add company
            </Button>
            <div className="flex flex-col gap-2 md:flex-row md:items-center">
              <TextInput
                sizing="sm"
                placeholder="Filter rows"
                value={rowQuery}
                onChange={(e) => setRowQuery(e.target.value)}
              />
              <Label className="flex items-center gap-2 text-sm text-gray-700">
                <Checkbox
                  checked={unmatchedOnly}
                  onChange={(e) => setUnmatchedOnly(e.target.checked)}
                />
                Unmatched only
              </Label>
            </div>
          </div>
          <div className="overflow-x-auto bg-white border rounded">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 text-left">
                <tr>
                  <th className="p-2">Read from map</th>
                  <th className="p-2">Member</th>
                  <th className="p-2">Name to show</th>
                </tr>
              </thead>
              <tbody>
                {visibleBooths.length === 0 ? (
                  <tr>
                    <td className="p-2 text-gray-500" colSpan={3}>
                      {booths.length === 0 ? "No companies yet." : "No rows match this filter."}
                    </td>
                  </tr>
                ) : (
                  visibleBooths.map(({ booth, index }) => (
                    <tr key={`${booth.label}-${index}`} className={`border-t ${selectedIndex === index ? "bg-blue-50" : ""}`}>
                      <td className="p-2 min-w-[12rem]">
                        <TextInput
                          sizing="sm"
                          value={booth.label}
                          placeholder="Company name"
                          onFocus={() => setSelectedIndex(index)}
                          onChange={(e) =>
                            updateBooth(index, {
                              label: e.target.value,
                              displayName:
                                !booth.displayName || booth.displayName === booth.label
                                  ? e.target.value
                                  : booth.displayName,
                            })
                          }
                        />
                      </td>
                      <td className="p-2 min-w-[16rem]">
                        <ReactSelect
                          isClearable
                          isSearchable
                          options={memberOptions}
                          placeholder="Search members"
                          noOptionsMessage={() =>
                            memberOptions.length === 0 ? "Loading members…" : "No members found"
                          }
                          value={booth.memberId ? optionById.get(booth.memberId) ?? null : null}
                          onChange={(option) =>
                            updateBooth(index, {
                              memberId: option?.value ?? null,
                              label: booth.label.trim() ? booth.label : option?.label ?? "",
                              displayName: option?.label ?? booth.label,
                            })
                          }
                          {...solidMenuReactSelectProps}
                        />
                      </td>
                      <td className="p-2">
                        <TextInput
                          value={booth.displayName ?? ""}
                          onChange={(e) => updateBooth(index, { displayName: e.target.value })}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <Button type="button" disabled={busy || !floorplanUrl} onClick={save}>
        {busy ? "Working…" : "Save map"}
      </Button>
    </div>
  );
});

export default ContactSessionEditor;
